import React, { useContext, useState, useRef, useEffect } from 'react'
import SponsoredSignUp from 'components/forms/SponsoredSignUp'
import SignIn from 'components/forms/SignIn'
import { userContext } from 'global/context/sub-contexts/userContext'
import { modalContext } from 'global/context/sub-contexts/modalContext'
import { Formik } from 'formik'
import { login, register } from 'services/auth'
import { getToken } from 'services/user'
import analytics from 'global/AnalyticsData'
import { navigate } from 'gatsby'
import sessionStorageWrapper from 'util/SessionStorageWrapper'
import localStorageWrapper from 'util/LocalStorageWrapper'
import {
  validateSignUpForm,
  validateSignInForm,
} from './components/validation.js'
import { Captcha } from './components/Captcha.jsx'
import { CaptchaV3 } from './components/CaptchaV3.jsx'
import { useFingerPrint } from 'hooks/useFingerPrint.jsx'

const formatAPIErrors = result => {
  try {
    if (result.message) {
      return result.message
    }

    let errorMessages = result.detail // default

    if (result.detail.errors) {
      errorMessages = result.detail.errors.reduce(
        (accumulator, currentValue) => {
          return (accumulator += ` - ${currentValue.defaultMessage}<br/>`)
        },
        '',
      )
    } else {
      errorMessages = result.detail.message
    }
    return errorMessages
  } catch (e) {
    return `Error formatting API errors: ${e}`
  }
}

const SignInUpForm = ({
  closeModal,
  tab,
  redirect,
  onRegistrationSuccess,
  onLoginSuccess,
}) => {
  const fingerPrintResults = useFingerPrint()
  const user_context = useContext(userContext)
  const modal_context = useContext(modalContext)
  const recaptchaRef = useRef()
  const recaptchaV3Ref = useRef()

  const crudelyHideRefErrors = () => {
    try {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
        recaptchaV3Ref.current.reset()
      }
    } catch (e) {
      console.log(`error resetting recaptcha: ${e.toString()}`)
    }
  }

  const { setSignInUpModal, showModalTemplate } = modal_context || {}

  const [resetSwipe, setResetSwipe] = useState(false)
  const [isInProgress, setIsInProgress] = useState(false)
  const [loginError, setLoginError] = useState()
  const [registrationError, setRegistrationError] = useState()
  const [currentTab, setCurrentTab] = useState(tab)

  const handleLogin = (username, password, seekEmail) => {
    const visitorId = fingerPrintResults?.visitorId || null
    login(username, password, visitorId, seekEmail).then(result => {
      if (result.success === true) {
        user_context.setProfile(result.profile)
        user_context.setPoints(result.profile.points)

        analytics.setUser(result.profile)

        if (typeof window !== `undefined`) {
          let user = result
          window.dataLayer.push({
            event: 'login',
            user: {
              uid: user.id,
              age: user.age,
              date_of_registration: user.registrationDate,
              is_guest: user.guest,
              zip: user.zip,
              gender: user.gender,
              state: user.state,
              original_utm_campaign: user.utm_campaign,
              original_utm_content: user.utm_content,
              original_utm_source: user.utm_source,
              original_utm_medium: user.utm_medium,
              original_utm_term: user.utm_term,
              utm_campaign: sessionStorageWrapper.getItem('utm_campaign'),
              utm_content: sessionStorageWrapper.getItem('utm_content'),
              utm_source: sessionStorageWrapper.getItem('utm_source'),
              utm_medium: sessionStorageWrapper.getItem('utm_medium'),
              utm_term: sessionStorageWrapper.getItem('utm_term'),
            },
          })
        }

        getToken()
          .then(data => {
            if (data.status === 200) {
              localStorageWrapper.setItem(
                'accessToken',
                data.detail.accessToken,
              )
            }

            if (onLoginSuccess) {
              if (redirect) {
                navigate(redirect)
              }
              onLoginSuccess()
            }
          })
          .catch(e => {
            // not getting a legacy token isn't great, but we don't need to fail
            console.log(`getToken`, e)
          })

        setSignInUpModal(false)
      } else {
        let errorMsg = 'Failed to login to account.' || result.message
       if (result.message) {
        errorMsg = result.message
       }
        setLoginError(errorMsg)
      }
    })
  }

  const handleRegistration = (
    email,
    password,
    birthDate,
    gender,
    storeIds,
    captchaResponseToken,
    captchaResponseTokenV3,
    seekEmail,
    // honeyPot field
  ) => {
    //NOTE NOVEMBER 22: API requires a date that looks like this: "YYYY-MM-DD"

    //TODO: why would this happen?
    if (isInProgress) {
      return
    }

    try {
      setIsInProgress(true)

      register({
        email,
        password,
        captchaResponseToken,
        captchaResponseTokenV3,
        birthDate,
        gender,
        storeIds,
        visitorId: fingerPrintResults?.visitorId
          ? fingerPrintResults.visitorId
          : null,
        seekEmail,
      })
        .then(result => {
          setIsInProgress(false)
          if (result.success === true) {
            user_context.setProfile(result.detail.user)
            user_context.setPoints(result.detail.user.points)

            analytics.setUser(result.detail.user)
            // analytics.trackEvent("registration")

            if (typeof window !== `undefined`) {
              let user = result.detail
              window.dataLayer.push({
                event: 'registration',
                user: {
                  uid: user.id,
                  age: user.age,
                  // "date_of_registration":"2018-05-29T15:04:51.361+03:00",
                  is_guest: user.guest,
                  zip: user.zip,
                  gender: user.gender,
                  state: user.state,
                  original_utm_campaign: user.utm_campaign,
                  original_utm_content: user.utm_content,
                  original_utm_source: user.utm_source,
                  original_utm_medium: user.utm_medium,
                  original_utm_term: user.utm_term,
                  utm_campaign: sessionStorageWrapper.getItem('utm_campaign'),
                  utm_content: sessionStorageWrapper.getItem('utm_content'),
                  utm_source: sessionStorageWrapper.getItem('utm_source'),
                  utm_medium: sessionStorageWrapper.getItem('utm_medium'),
                  utm_term: sessionStorageWrapper.getItem('utm_term'),
                },
              })
            }

            setSignInUpModal(false)

            if (onRegistrationSuccess) {
              onRegistrationSuccess()
            } else {
              showModalTemplate('completeprofile')
            }
          } else {
            if (result.status === 400 || result.hasError) {
              // the error responses can take 2 different shapes :/
              let errorMessages = formatAPIErrors(result)
              setRegistrationError(
                `We're sorry, an unexpected error has occurred. Please try again later. (errors: ${errorMessages})`,
              )
            } else {
              let errorMessages = formatAPIErrors(result)
              setRegistrationError(
                `We're sorry, an unexpected error has occurred. Please try again later. (result: ${errorMessages})`,
              )
            }
          }
          setResetSwipe(true)
          crudelyHideRefErrors()
        })
        .catch(e => {
          setResetSwipe(true)
          // if the email validation fails, api sends a 400 with the detail in the body
          // common.js swallows the error response details so we can't really tell the user what went wrong :(
          setIsInProgress(false)
          setRegistrationError(
            `We're sorry, an unexpected error has occurred. Please try again later. (e: ${e})`,
          )
          crudelyHideRefErrors()
        })
    } catch (e) {
      setResetSwipe(true)
      setIsInProgress(false)
      setRegistrationError(
        `We're sorry, an unexpected error has occurred. Please try again later. (e: ${e})`,
      )
      crudelyHideRefErrors()
    }
  }

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        birthDate: '',
        gender: '',
        storeIds: [],
        tab: currentTab,
        captchaResponseToken: null,
      }}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={values => {
        values.tab === 0 &&
          handleRegistration(
            values.email,
            values.password,
            values.birthDate,
            values.gender,
            values.storeIds,
            values.captchaResponseToken,
            values.captchaResponseTokenV3,
            values.seekEmail,
          )
        values.tab === 1 &&
          handleLogin(
            values.email,
            values.password,
            values.seekEmail,
          )
      }}
      validationSchema={
        currentTab === 0 ? validateSignUpForm : validateSignInForm
      }
    >
      {({ values, handleSubmit, setFieldValue, errors, touched, isValid }) => {
        return (
          <div className="modal-wrap">
            <form
              onSubmit={handleSubmit}
              id="signinup"
              className={`in-progress-${isInProgress}`}
            >
              <div className="modal__tabs">
                <ul className="modal__tabs-list">
                  <li className={`col-6  ${values.tab === 0 ? 'active' : ''}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setFieldValue('tab', 0)
                        setCurrentTab(0)
                      }}
                    >
                      Sign Up
                    </button>
                  </li>
                  <li className={`col-6  ${values.tab === 1 ? 'active' : ''}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setFieldValue('tab', 1)
                        setCurrentTab(1)
                      }}
                    >
                      Sign In
                    </button>
                  </li>
                </ul>
              </div>
              <div className="modal__tabs-content">
                <div className="form__wrapper">
                  {values.tab === 0 && (
                    <SponsoredSignUp
                      values={values}
                      setFieldValue={setFieldValue}
                      touched={touched}
                      errors={errors}
                      registrationError={registrationError}
                      recaptchaRef={recaptchaRef}
                      recaptchaV3Ref={recaptchaV3Ref}
                      Captcha={Captcha}
                      CaptchaV3={CaptchaV3}
                      closeModal={closeModal}
                      onSubmit={handleSubmit}
                      resetSwipe={resetSwipe || !registrationError || !isValid}
                      onResetSwipe={() => setResetSwipe(false)}
                    />
                  )}
                  {values.tab === 1 && (
                    <SignIn
                      values={values}
                      setFieldValue={setFieldValue}
                      touched={touched}
                      errors={errors}
                      loginError={loginError}
                      clearLoginError={() => setLoginError("")}
                      closeModal={closeModal}
                      isLoggingIn={isInProgress}
                    />
                  )}
                </div>
              </div>
            </form>
          </div>
        )
      }}
    </Formik>
  )
}

export default SignInUpForm
